import type { AnioFlujo, LineaCotizacion, ResultadoCotizacion } from '../api'
import { num, pct, usd, usdExacto } from '../formato'
import { CeldaCosto } from './CeldaCosto'
import { porcentaje } from './CampoUtilidad'
import { PARTIDAS_QUITABLES } from './PartidasQuitadas'

interface PropsLineas {
  lineas: LineaCotizacion[]
  totales: ResultadoCotizacion['totales']
  /** Si se da, las partidas secundarias muestran «Quitar» y la cotizacion se recalcula sin ellas. */
  alQuitar?: (codigo: string) => void
  /** Si se da, el «Costo u.» de cada linea se puede cambiar para esta cotizacion. */
  costos?: {
    cambiados: Record<string, number>
    alCambiar: (codigo: string, nuevo: number) => void
    alRestablecer: (codigo: string) => void
  }
  /** Si se da, aparece la columna «Utilidad %» y la de cada linea se puede cambiar (en %, no en fraccion). */
  margenes?: {
    cambiados: Record<string, number>
    alCambiar: (codigo: string, fraccion: number) => void
    alRestablecer: (codigo: string) => void
  }
}

export function TablaLineas({ lineas, totales: t, alQuitar, costos, margenes }: PropsLineas) {
  // Las filas del pie dejan libres las ultimas dos columnas (Subtotal e IVA).
  const etiqueta = margenes ? 5 : 4
  // Con comision, los rubros vienen SIN ella y la tabla la suma en una fila aparte.
  const conComision = (t.comision ?? 0) > 0 && t.subtotal_sin_comision !== undefined
  return (
    <div className="tabla-envoltura">
      <table className="tabla compacta">
        <thead>
          <tr>
            <th>Concepto</th>
            <th className="num">Cant.</th>
            <th className="num">Costo u.</th>
            {margenes && <th className="num">Utilidad %</th>}
            <th className="num">Precio u.</th>
            <th className="num">Subtotal</th>
            <th className="num">IVA</th>
          </tr>
        </thead>
        <tbody>
          {lineas.map((l) => (
            <tr key={l.codigo} className="sin-clic">
              <td>
                {l.nombre}
                <small className="suave bloque">
                  {l.codigo} · {l.proveedor}
                  {l.desde_bodega > 0 && ` · ${l.desde_bodega} en bodega`}
                  {/ESTIMADA/.test(l.fuente_costo) && <span className="insignia insignia-estimada"> cantidad estimada</span>}
                </small>
                {alQuitar && PARTIDAS_QUITABLES.has(l.codigo) && (
                  <button type="button" className="enlace pequeno" onClick={() => alQuitar(l.codigo)}>
                    Quitar
                  </button>
                )}
              </td>
              <td className="num">
                {num(l.cantidad)} <small className="suave">{l.unidad}</small>
              </td>
              {costos ? (
                <td className="num">
                  <CeldaCosto
                    valor={l.costo_unitario}
                    modificado={l.codigo in costos.cambiados}
                    alCambiar={(n) => costos.alCambiar(l.codigo, n)}
                    alRestablecer={() => costos.alRestablecer(l.codigo)}
                  />
                </td>
              ) : (
                <td className="num suave">{usdExacto(l.costo_unitario)}</td>
              )}
              {margenes && (
                <td className="num">
                  {l.margen === undefined ? (
                    <span className="suave">—</span>
                  ) : (
                    <CeldaCosto
                      valor={porcentaje(l.margen)}
                      modificado={l.codigo in margenes.cambiados}
                      alCambiar={(n) => margenes.alCambiar(l.codigo, n / 100)}
                      alRestablecer={() => margenes.alRestablecer(l.codigo)}
                      etiqueta="Utilidad sobre costo (%)"
                      tituloRestablecer="Volver a la utilidad general"
                      maximo={200}
                    />
                  )}
                </td>
              )}
              <td className="num">{usdExacto(l.precio_unitario)}</td>
              <td className="num fuerte">{usdExacto(l.subtotal)}</td>
              <td className="num suave">{pct(l.iva_pct, 0)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {conComision && (
            <>
              <tr>
                <td colSpan={etiqueta}>Subtotal sin comisión</td>
                <td className="num" colSpan={2}>{usdExacto(t.subtotal_sin_comision!)}</td>
              </tr>
              <tr className="interno">
                <td colSpan={etiqueta}>
                  Comisión del vendedor ({pct(t.comision_pct ?? 0)}) — <em>en el PDF va repartida en cada rubro</em>
                </td>
                <td className="num" colSpan={2}>{usdExacto(t.comision!)}</td>
              </tr>
            </>
          )}
          <tr>
            <td colSpan={etiqueta}>{conComision ? 'Subtotal con comisión' : 'Subtotal'}</td>
            <td className="num" colSpan={2}>{usdExacto(t.subtotal_sin_descuento)}</td>
          </tr>
          {t.valor_descuento > 0 && (
            <tr>
              <td colSpan={etiqueta}>Descuento ({pct(t.descuento_pct)})</td>
              <td className="num" colSpan={2}>−{usdExacto(t.valor_descuento)}</td>
            </tr>
          )}
          <tr>
            <td colSpan={etiqueta}>Base imponible</td>
            <td className="num" colSpan={2}>{usdExacto(t.base_imponible)}</td>
          </tr>
          <tr>
            <td colSpan={etiqueta}>IVA</td>
            <td className="num" colSpan={2}>{usdExacto(t.iva)}</td>
          </tr>
          <tr className="total">
            <td colSpan={etiqueta}>Total</td>
            <td className="num" colSpan={2}>{usdExacto(t.total)}</td>
          </tr>
          <tr className="interno">
            <td colSpan={etiqueta}>
              Costo SunSmart · utilidad ({pct(t.utilidad_pct)} sobre costo) — <em>solo interno</em>
            </td>
            <td className="num" colSpan={2}>
              {usd(t.costo_total)} · {usd(t.utilidad)}
            </td>
          </tr>
          {(t.comision ?? 0) > 0 && !conComision && (
            // Version guardada antes de que el motor diera el subtotal sin comision: sus rubros ya la llevan.
            <tr className="interno">
              <td colSpan={etiqueta}>
                Comisión del vendedor ({pct(t.comision_pct ?? 0)}, sin IVA, ya incluida en cada rubro) — <em>solo interno</em>
              </td>
              <td className="num" colSpan={2}>{usd(t.comision ?? 0)}</td>
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  )
}

export function TablaFlujo({ anios }: { anios: AnioFlujo[] }) {
  return (
    <div className="tabla-envoltura">
      <table className="tabla compacta">
        <thead>
          <tr>
            <th>Año</th>
            <th className="num">Generación</th>
            <th className="num">Ahorro</th>
            <th className="num">Mantenimiento</th>
            <th className="num">Flujo neto</th>
            <th className="num">Acumulado</th>
          </tr>
        </thead>
        <tbody>
          {anios.map((a) => (
            <tr key={a.anio} className="sin-clic">
              <td>{a.anio}</td>
              <td className="num">{num(a.generacion_kwh)} kWh</td>
              <td className="num">{usd(a.ingreso)}</td>
              <td className="num suave">{usd(a.opex)}</td>
              <td className="num">{usd(a.flujo_neto)}</td>
              <td className={a.flujo_acumulado >= 0 ? 'num positivo' : 'num negativo'}>{usd(a.flujo_acumulado)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
